import type { CustomEventName } from '../utils/tracking'
import type { ProductLinkKey } from './productLinks'
import type { FlowKind, SelectionSource } from '../utils/funnelState'

/*
 * Checkout negotiation · FINAL PRICING LOCK. Cart links live in data/productLinks.ts.
 *
 * Only one flow runs per checkout, each flow at most once per session, and an
 * accepted upgrade never automatically opens another ladder.
 *
 * Standard U.S. shipping is free on every order.
 *
 *   1 X1, no lifetime ($49.95)                → 3 for $99.90, with "Only want 1?" $59.90 lifetime
 *                                                 on the same screen
 *                                               → $57.90 lifetime (final offer)
 *                                               → standard $49.95 · stop (opens at checkout)
 *   1 X1 + lifetime ($69.90)                  → at checkout: $99.90 → $69.90 · stop
 *   2 X1 ($99.90)                             → free third X1 → optional crew pricing link
 *   Direct 3 X1 ($99.90)                      → 6 for $169.90 → 5 for $144.90 → 4 for $119.90 · stop
 *   Direct 6 X1 ($179.90)                     → 12 for $299.90 → 10 for $259.90 → 8 for $219.90 · stop
 *   Direct 30 X1 ($699.90)                    → 60 for $1,299.90 → 50 for $1,099.90 → 40 for $899.90 · stop
 *
 * Public ladder per X1: 1 = $49.95 · 3 = $33.30 · 6 = $29.98 · 30 = $23.33.
 *
 * Every order ships free in the U.S., so no offer depends on a shipping estimate.
 */

export const SINGLE_PRICE = 49.95
export const LIFETIME_UPGRADE_PRICE = 19.95
export const SINGLE_WITH_LIFETIME_PRICE = 69.9
export const TRIO_PRICE = 99.9
export const CREW_PRICE = 179.9
export const BUSINESS_PRICE = 699.9
/** Two public 30-packs recognized as one 60-system deployment (private business price). */
export const SIXTY_PRICE = 1299.9


const fmt = (value: number) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/** Where a button finally sends the customer. Always one cart line: accepting replaces, never stacks. */
export interface CartTarget {
  link: ProductLinkKey
  quantity: number
  name: string
  /** Product total for this cart line, before shipping and tax. */
  value: number
}

export interface OrderSummary {
  label: string
  detail: string
  total: number
  coverage: string
  /** true = free standard shipping; false = calculated at checkout (typically around $6.95) */
  freeShipping: boolean
}

/** Shown after accepting a step instead of routing straight to the cart. */
export interface AcceptConfirmation {
  headline: string
  body: string
  cta: string
  /** Non-blocking, customer-initiated next opportunity. */
  followUp?: { label: string; flow: FlowKind; source: SelectionSource }
}

export interface UpsellStep {
  id: string
  plate: string
  headline: string
  highlight: string
  intro: string
  current: OrderSummary
  upgrade: OrderSummary
  /** Headline difference: "Same price" / "About $23 more before tax*" / "$70 more". */
  extra: string
  /** Exact product-price change, the estimated shipping saved, and the approximate real difference. */
  economics?: { productDiff: string; shippingSaved: string; result: string }
  includes: string[]
  /** Warranty price ladder on the lifetime-only offers. */
  valueLine?: { normal: string; today: string; todayLabel: string; shipping: string }
  /** Spells out that the number shown is the new product total, not the extra charge. */
  clarification: string
  lifetimeMedal?: boolean
  accept: string
  decline: string
  target: CartTarget
  /** Legacy per-step event names. Generic upsell_offer_* events always fire too. */
  events?: { viewed: CustomEventName; accepted: CustomEventName; declined: CustomEventName }
  confirmOnAccept?: AcceptConfirmation
  /** A second, smaller offer on the same screen ("Only want 1? That's fine."). */
  alternate?: AlternateOffer
}

export interface AlternateOffer {
  eyebrow: string
  headline: string
  body: string
  summary: OrderSummary
  /** Exact product-price change plus approximate real difference. */
  extra: string
  clarification: string
  cta: string
  target: CartTarget
  events?: { viewed: CustomEventName; accepted: CustomEventName }
}

export interface UpsellFlow {
  kind: FlowKind
  source: SelectionSource
  steps: UpsellStep[]
  /** After the last "no thanks": exactly what the customer originally picked. */
  fallback: CartTarget
}

const packSummary = (units: number, total: number): OrderSummary => ({
  label: `${units} X1 complete systems`,
  detail: `${units * 3} lenses · ${fmt(total / units)} per X1`,
  total,
  coverage: `Lifetime on all ${units}`,
  freeShipping: true,
})

const trioIncludes = [
  '3 complete X1 systems',
  '9 interchangeable lenses: 3 Clear, 3 Amber, 3 Smoke',
  'Lifetime Replacement Warranty on all 3',
  'Free standard shipping',
  'Free mystery gift',
]

// ---------------------------------------------------------------------------
// 1 X1 without lifetime
// ---------------------------------------------------------------------------

function singleNoLifetimeFlow(source: SelectionSource): UpsellFlow {
  const current: OrderSummary = {
    label: '1 X1 complete system',
    detail: '$49.95 · free U.S. shipping',
    total: SINGLE_PRICE,
    coverage: 'One-year coverage',
    freeShipping: true,
  }
  return {
    kind: 'single-no-lifetime',
    source,
    steps: [
      {
        id: 'single-to-trio-9990',
        plate: 'Before you check out',
        headline: 'Take 3. Everything included.',
        highlight: '3 for $99.90.',
        intro:
          'Your X1 is $49.95. For $49.95 more, take 3 complete X1 systems, 9 lenses, lifetime coverage on all 3 and a mystery gift. Just $33.30 per X1.',
        current,
        upgrade: { ...packSummary(3, TRIO_PRICE), label: '3 X1 + Lifetime on all 3' },
        extra: '$49.95 more',
        includes: trioIncludes,
        clarification: 'You are not paying $99.90 more. Your new product total is $99.90.',
        lifetimeMedal: true,
        accept: 'Yes, get 3 for $99.90',
        decline: 'No thanks, I just want my X1',
        target: { link: 'threeX1Public9990', quantity: 1, name: 'Buy 2, Get 1 Free · 3 complete systems', value: TRIO_PRICE },
        events: {
          viewed: 'single_to_trio_viewed',
          accepted: 'single_to_trio_accepted',
          declined: 'single_to_trio_declined',
        },
        alternate: {
          eyebrow: "Only want 1? That's fine.",
          headline: 'Keep 1. Protect it for life.',
          body: 'Your X1 + Lifetime Replacement Warranty for $9.95 instead of the normal $19.95. $49.95 + $9.95 = $59.90.',
          summary: {
            label: '1 X1 + Lifetime',
            detail: '$49.95 + $9.95 = $59.90 total',
            total: 59.9,
            coverage: 'Lifetime coverage',
            freeShipping: true,
          },
          extra: '+$9.95',
          clarification: 'Your new product total is $59.90.',
          cta: 'Yes, 1 X1 + lifetime for $59.90',
          target: {
            link: 'singleX1LifetimePrivate5990',
            quantity: 1,
            name: 'GritVue X1 Complete System + Lifetime Replacement Warranty + Free Shipping',
            value: 59.9,
          },
          events: { viewed: 'single_lifetime_995_viewed', accepted: 'single_lifetime_995_accepted' },
        },
      },
      {
        id: 'single-lifetime-5790',
        plate: 'One final deal',
        headline: 'One final deal.',
        highlight: 'Lifetime coverage for $7.95.',
        intro:
          'Keep your X1 and protect it for life. Get the Lifetime Replacement Warranty for $7.95 instead of the normal $19.95.',
        current,
        upgrade: {
          label: '1 X1 + Lifetime',
          detail: '$49.95 + $7.95 = $57.90 total',
          total: 57.9,
          coverage: 'Lifetime coverage',
          freeShipping: true,
        },
        extra: '$7.95 more',
        includes: [
          'Everything in your current order',
          'Lifetime Replacement Warranty. Break it. We replace it. Terms apply.',
          'Free U.S. shipping, as always',
        ],
        valueLine: { normal: '$19.95', today: '$7.95', todayLabel: 'Final private upgrade', shipping: 'Free' },
        clarification: 'You are not paying $57.90 more. Your new product total is $57.90.',
        lifetimeMedal: true,
        accept: 'Yes, protect my X1 for life',
        decline: 'No thanks, continue with $49.95',
        target: {
          link: 'singleX1LifetimeFinal5790',
          quantity: 1,
          name: 'GritVue X1 Complete System + Lifetime Replacement Warranty + Free Shipping · final offer',
          value: 57.9,
        },
        events: {
          viewed: 'single_lifetime_795_viewed',
          accepted: 'single_lifetime_795_accepted',
          declined: 'single_lifetime_795_declined',
        },
      },
    ],
    fallback: { link: 'singleX1', quantity: 1, name: '1 GritVue X1 Complete System', value: SINGLE_PRICE },
  }
}

// ---------------------------------------------------------------------------
// 1 X1 + lifetime · only when the customer taps "Want free shipping?"
// ---------------------------------------------------------------------------

function singleLifetimeShippingFlow(source: SelectionSource): UpsellFlow {
  const current: OrderSummary = {
    label: '1 X1 + Lifetime',
    detail: '$69.90 · free U.S. shipping',
    total: SINGLE_WITH_LIFETIME_PRICE,
    coverage: 'Lifetime coverage',
    freeShipping: true,
  }
  return {
    kind: 'single-lifetime-shipping',
    source,
    steps: [
      {
        id: 'lifetime-trio-9990',
        plate: 'Before you check out',
        headline: 'Why protect just one?',
        highlight: 'Get 3 for $99.90.',
        intro:
          'For $30 more, get 3 complete X1 systems instead of one, lifetime coverage on all 3 and 9 interchangeable lenses. Your 3rd X1 is free.',
        current,
        upgrade: { ...packSummary(3, TRIO_PRICE), label: '3 X1 + Lifetime on all 3' },
        extra: '$30 more',
        includes: trioIncludes,
        clarification: 'You are not paying $99.90 more. Your new product total is $99.90.',
        lifetimeMedal: true,
        accept: 'Yes, get 3 for $99.90',
        decline: 'No thanks, keep my current order',
        target: { link: 'threeX1Public9990', quantity: 1, name: 'Buy 2, Get 1 Free · 3 complete systems', value: TRIO_PRICE },
        events: {
          viewed: 'three_x1_lifetime_9990_viewed',
          accepted: 'three_x1_lifetime_9990_accepted',
          declined: 'three_x1_lifetime_9990_declined',
        },
      },
      // The old "$69.90 + free shipping" retention step is gone: every order now ships free.
    ],
    fallback: {
      link: 'singleX1LifetimeNormal6990',
      quantity: 1,
      name: 'GritVue X1 Complete System + Lifetime Replacement Warranty',
      value: SINGLE_WITH_LIFETIME_PRICE,
    },
  }
}

// ---------------------------------------------------------------------------
// 2 X1 → free third
// ---------------------------------------------------------------------------

function twoSystemsFlow(source: SelectionSource): UpsellFlow {
  return {
    kind: 'two-systems',
    source,
    steps: [
      {
        id: 'free-third',
        plate: 'You just unlocked a free X1',
        headline: 'Congratulations.',
        highlight: 'Your 3rd X1 is free.',
        intro: 'Why take 2 when you can get 3 for the same $99.90?',
        current: {
          label: '2 complete X1 systems',
          detail: '6 lenses',
          total: TRIO_PRICE,
          coverage: 'One-year coverage',
          freeShipping: true,
        },
        upgrade: {
          label: '3 complete X1 systems',
          detail: '9 lenses · mystery gift',
          total: TRIO_PRICE,
          coverage: 'Lifetime on all 3',
          freeShipping: true,
        },
        extra: 'Same product price',
        includes: [
          '3 complete X1 systems',
          '9 interchangeable lenses: 3 Clear, 3 Amber, 3 Smoke',
          'Lifetime Replacement Warranty on all 3 systems',
          'Free standard shipping',
          'Free mystery gift',
        ],
        clarification: 'Your 3rd X1 is completely free. You pay the same $99.90.',
        lifetimeMedal: true,
        accept: 'Claim my free X1',
        decline: 'No thanks, I only want 2 for $99.90',
        target: { link: 'threeX1Public9990', quantity: 1, name: 'Buy 2, Get 1 Free · 3 complete systems', value: TRIO_PRICE },
        events: {
          viewed: 'two_x1_free_third_viewed',
          accepted: 'two_x1_free_third_accepted',
          declined: 'two_x1_free_third_declined',
        },
        confirmOnAccept: {
          headline: 'Your 3rd X1 is free.',
          body: '3 complete X1 systems · 9 lenses · lifetime coverage on all 3 · free standard shipping · $99.90',
          cta: 'Continue to checkout',
          followUp: {
            label: 'Need more for your crew? See private crew pricing',
            flow: 'trio-expansion',
            source: 'free_third_claimed',
          },
        },
      },
    ],
    fallback: { link: 'singleX1', quantity: 2, name: '2 GritVue X1 complete systems', value: TRIO_PRICE },
  }
}

// ---------------------------------------------------------------------------
// Quantity expansion ladders (all free shipping, so every difference is exact)
// ---------------------------------------------------------------------------

interface Rung {
  units: number
  total: number
  link: ProductLinkKey
  plate: string
  headline: string
  highlight: string
  intro: string
  accept: string
}

interface LadderBase {
  units: number
  total: number
  link: ProductLinkKey
  name: string
  decline: string
}

function expansionFlow(kind: FlowKind, source: SelectionSource, base: LadderBase, rungs: Rung[]): UpsellFlow {
  const current = packSummary(base.units, base.total)
  return {
    kind,
    source,
    steps: rungs.map((rung) => ({
      id: `${kind}-${rung.units}`,
      plate: rung.plate,
      headline: rung.headline,
      highlight: rung.highlight,
      intro: rung.intro,
      current,
      upgrade: packSummary(rung.units, rung.total),
      extra: `${fmt(rung.total - base.total)} more`,
      includes: [
        `${rung.units} complete X1 systems`,
        `${rung.units * 3} interchangeable lenses`,
        `Lifetime Replacement Warranty on all ${rung.units}`,
        'Free standard shipping',
      ],
      clarification: `You are not paying ${fmt(rung.total)} more. Your new product total is ${fmt(rung.total)}.`,
      lifetimeMedal: true,
      accept: rung.accept,
      decline: base.decline,
      target: { link: rung.link, quantity: 1, name: `GritVue X1 · ${rung.units} complete systems · private offer`, value: rung.total },
    })),
    fallback: { link: base.link, quantity: 1, name: base.name, value: base.total },
  }
}

function trioExpansionFlow(source: SelectionSource) {
  return expansionFlow(
    'trio-expansion',
    source,
    { units: 3, total: TRIO_PRICE, link: 'threeX1Public9990', name: 'Buy 2, Get 1 Free · 3 complete systems', decline: 'No thanks, keep my 3' },
    [
      {
        units: 6,
        total: 169.9,
        link: 'sixX1Private16990',
        plate: 'Private crew pricing',
        headline: 'Double your setup.',
        highlight: '6 for $169.90.',
        intro:
          "You're already taking 3. For $70 more, take 6 complete X1 systems, 18 interchangeable lenses, lifetime coverage on all 6 and free standard shipping. About $28.32 per X1.",
        accept: 'Yes, get 6 for $169.90',
      },
      {
        units: 5,
        total: 144.9,
        link: 'fiveX1Private14490',
        plate: 'Private crew pricing',
        headline: 'Not ready for 6?',
        highlight: 'Take 5.',
        intro: 'Two extra complete X1 systems, lifetime coverage on all 5 and free standard shipping. Only $45 more than your current 3-pack.',
        accept: 'Yes, get 5 for $144.90',
      },
      {
        units: 4,
        total: 119.9,
        link: 'fourX1Private11990',
        plate: 'Final crew offer',
        headline: 'One more X1.',
        highlight: 'Only $20 more.',
        intro: 'Take 4 complete systems instead of 3, with lifetime coverage on all 4 and free standard shipping.',
        accept: 'Yes, get 4 for $119.90',
      },
    ],
  )
}

function sixExpansionFlow(source: SelectionSource) {
  return expansionFlow(
    'six-expansion',
    source,
    { units: 6, total: CREW_PRICE, link: 'sixX1Public17990', name: 'Buy 4, Get 2 Free · 6 complete systems', decline: 'No thanks, keep my 6' },
    [
      {
        units: 12,
        total: 299.9,
        link: 'twelveX1Private29990',
        plate: 'Private crew pricing',
        headline: 'Double the crew.',
        highlight: '12 for $299.90.',
        intro:
          "You're already equipping 6. Move to 12 complete X1 systems, 36 lenses, lifetime coverage on all 12 and free standard shipping. About $24.99 per X1.",
        accept: 'Yes, get 12 for $299.90',
      },
      {
        units: 10,
        total: 259.9,
        link: 'tenX1Private25990',
        plate: 'Private crew pricing',
        headline: 'Not ready for 12?',
        highlight: 'Take 10.',
        intro: 'Four more complete X1 systems, 30 lenses in total, lifetime coverage on all 10 and free standard shipping. $80 more than your current order.',
        accept: 'Yes, get 10 for $259.90',
      },
      {
        units: 8,
        total: 219.9,
        link: 'eightX1Private21990',
        plate: 'Final crew offer',
        headline: 'Final crew upgrade.',
        highlight: 'Add 2 more.',
        intro: 'Add 2 more complete X1 systems for $40 more, with lifetime coverage on all 8 and free standard shipping.',
        accept: 'Yes, get 8 for $219.90',
      },
    ],
  )
}

function businessExpansionFlow(source: SelectionSource) {
  return expansionFlow(
    'business-expansion',
    source,
    { units: 30, total: BUSINESS_PRICE, link: 'thirtyX1Public69990', name: 'Buy 20, Get 10 Free · 30 complete systems', decline: 'No thanks, keep my 30' },
    [
      {
        units: 60,
        total: SIXTY_PRICE,
        link: 'sixtyX1BusinessPrivate129990',
        plate: 'Private deployment pricing',
        headline: 'Double your deployment.',
        highlight: 'Equip 60 for $1,299.90.',
        intro:
          '30 more complete X1 systems for $600 more than your current order. Save $99.90 versus buying two standard 30-packs ($1,399.80). About $21.67 per X1.',
        accept: 'Yes, equip 60 for $1,299.90',
      },
      {
        units: 50,
        total: 1099.9,
        link: 'fiftyX1BusinessPrivate109990',
        plate: 'Private deployment pricing',
        headline: "Let's scale it back.",
        highlight: 'Take 50.',
        intro: '20 more complete X1 systems, lifetime coverage on all 50 and free standard shipping. Only $400 more than your current order.',
        accept: 'Yes, equip 50 for $1,099.90',
      },
      {
        units: 40,
        total: 899.9,
        link: 'fortyX1BusinessPrivate89990',
        plate: 'Final deployment offer',
        headline: 'One final crew upgrade.',
        highlight: 'Add 10 more.',
        intro: 'Add 10 more complete X1 systems for only $200 more, with lifetime coverage on all 40 and free standard shipping.',
        accept: 'Yes, equip 40 for $899.90',
      },
    ],
  )
}

export function buildFlow(kind: FlowKind, source: SelectionSource): UpsellFlow {
  switch (kind) {
    case 'single-no-lifetime':
      return singleNoLifetimeFlow(source)
    case 'single-lifetime-shipping':
      return singleLifetimeShippingFlow(source)
    case 'two-systems':
      return twoSystemsFlow(source)
    case 'trio-expansion':
      return trioExpansionFlow(source)
    case 'six-expansion':
      return sixExpansionFlow(source)
    case 'business-expansion':
      return businessExpansionFlow(source)
  }
}

export type PackCard = 'single' | 'trio' | 'crew' | 'business'

/**
 * Which flow a checkout click opens. Ladders only run for a DIRECT choice of
 * that pack at quantity 1; a pack reached by accepting a suggestion or an
 * upgrade goes straight to the cart.
 */
export function checkoutFlow(
  card: PackCard,
  quantity: number,
  options: { lifetime?: boolean; reachedBySwitch?: boolean } = {},
): { kind: FlowKind; source: SelectionSource } | null {
  if (card === 'single') {
    if (quantity === 2) return { kind: 'two-systems', source: 'two_unit_selection' }
    if (quantity === 1 && options.lifetime) return { kind: 'single-lifetime-shipping', source: 'single_lifetime' }
    if (quantity === 1) return { kind: 'single-no-lifetime', source: 'single_standard' }
    return null
  }
  if (quantity !== 1 || options.reachedBySwitch) return null
  if (card === 'trio') return { kind: 'trio-expansion', source: 'direct_three_pack' }
  if (card === 'crew') return { kind: 'six-expansion', source: 'direct_six_pack' }
  return { kind: 'business-expansion', source: 'direct_thirty_pack' }
}

// ---------------------------------------------------------------------------
// Quantity arbitrage protection
// ---------------------------------------------------------------------------

/** Units and product total for any public card at any quantity. */
export function packTotal(card: PackCard, quantity: number): { units: number; total: number } {
  switch (card) {
    case 'single':
      return { units: quantity, total: SINGLE_PRICE * quantity }
    case 'trio':
      return { units: 3 * quantity, total: TRIO_PRICE * quantity }
    case 'crew':
      return { units: 6 * quantity, total: CREW_PRICE * quantity }
    case 'business':
      // Two 30-packs are recognized as one 60-system deployment.
      return quantity === 2 ? { units: 60, total: SIXTY_PRICE } : { units: 30 * quantity, total: BUSINESS_PRICE * quantity }
  }
}

export interface PackSuggestion {
  message: string
  action: string
  switchTo: 'trio' | 'crew' | 'business'
  /** Quantity to set on the target card. */
  targetQuantity: number
  /** true: the card's own checkout would charge more for the same or fewer units, so the switch replaces it. */
  replacesCheckout: boolean
}

const cardRank: Record<PackCard, number> = { single: 0, trio: 1, crew: 2, business: 3 }
const MAX_QTY: Record<PackCard, number> = { single: 20, trio: 20, crew: 20, business: 10 }

function describe(card: PackCard, quantity: number): string {
  if (card === 'trio') return quantity === 1 ? 'Buy 2, Get 1 Free' : `${quantity} × Buy 2, Get 1 Free`
  if (card === 'crew') return quantity === 1 ? 'Buy 4, Get 2 Free' : `${quantity} × Buy 4, Get 2 Free`
  if (card === 'business') {
    if (quantity === 1) return 'the Business / Crew Pack'
    if (quantity === 2) return 'one 60-system business deployment'
    return `${quantity} Business / Crew Packs`
  }
  return `${quantity} singles`
}

/**
 * Whenever a manual quantity would cost the same or more than a larger public
 * pack that gives at least as many X1 systems, the card's checkout is replaced
 * by a switch to that pack. Only approved public prices (and the approved
 * 60-system business price on two 30-packs) are ever used; nothing new is invented.
 */
export function suggestionFor(card: 'single' | 'trio' | 'crew', quantity: number): PackSuggestion | null {
  // 2 singles are handled by the free-third flow at checkout.
  if (quantity <= 1 || (card === 'single' && quantity === 2)) return null
  const { units, total } = packTotal(card, quantity)
  let best: { card: 'trio' | 'crew' | 'business'; quantity: number; units: number; total: number } | null = null
  for (const candidate of ['trio', 'crew', 'business'] as const) {
    if (cardRank[candidate] <= cardRank[card]) continue
    for (let q = 1; q <= MAX_QTY[candidate]; q += 1) {
      const option = packTotal(candidate, q)
      if (option.units < units) continue
      if (option.total <= total + 0.001 && (!best || option.total < best.total - 0.001)) {
        best = { card: candidate, quantity: q, ...option }
      }
      break // the first quantity that covers the units is always the cheapest for this card
    }
  }
  if (!best) return null
  return {
    message: `${describe(card, quantity)} would be ${fmt(total)} for ${units} X1. ${describe(best.card, best.quantity)} gives you ${best.units} X1 for ${fmt(best.total)}, with lifetime coverage on every unit and free shipping.`,
    action: `Switch to ${best.units} for ${fmt(best.total)}`,
    switchTo: best.card,
    targetQuantity: best.quantity,
    replacesCheckout: true,
  }
}
